import axios from 'axios';
import type {
  ScheduledTaskItem,
  ScheduleRunListItem,
  ScheduleRunDetailResponse,
  BatchJobItem,
  BatchExecutionListItem,
  JobExecutionDto,
  TriggerRequestResponse,
  PageResponse,
} from '../types/schedulerMonitor';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Bearer 토큰 부착 인터셉터
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ApiResponse<T> 언래핑 인터셉터
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? '알 수 없는 오류',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      '서버에 연결할 수 없습니다';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const schedulerMonitorApi = {
  /** GET /scheduler-monitor/schedules — 등록 스케줄 작업 목록 (SCHEDULER_READ) */
  listSchedules: (): Promise<ScheduledTaskItem[]> =>
    apiClient
      .get<unknown, { data: ScheduledTaskItem[] }>('/scheduler-monitor/schedules')
      .then((res) => res.data),

  /** GET /scheduler-monitor/schedules/runs — 실행 이력 페이징 (SCHEDULER_READ) */
  listScheduleRuns: (params: {
    taskName?: string;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<ScheduleRunListItem>> => {
    const sp = new URLSearchParams();
    if (params.taskName) sp.set('taskName', params.taskName);
    if (params.status) sp.set('status', params.status);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<ScheduleRunListItem> }>(
        `/scheduler-monitor/schedules/runs?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /scheduler-monitor/schedules/runs/{id} — 실행 이력 상세 (SCHEDULER_READ) */
  getScheduleRun: (id: number): Promise<ScheduleRunDetailResponse> =>
    apiClient
      .get<unknown, { data: ScheduleRunDetailResponse }>(
        `/scheduler-monitor/schedules/runs/${id}`
      )
      .then((res) => res.data),

  /** GET /scheduler-monitor/batch/jobs — 배치 작업 목록 (SCHEDULER_READ) */
  listBatchJobs: (): Promise<BatchJobItem[]> =>
    apiClient
      .get<unknown, { data: BatchJobItem[] }>('/scheduler-monitor/batch/jobs')
      .then((res) => res.data),

  /** GET /scheduler-monitor/batch/jobs/{jobName}/executions — 배치 실행 이력 (SCHEDULER_READ) */
  listBatchExecutions: (params: {
    jobName: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<BatchExecutionListItem>> => {
    const sp = new URLSearchParams();
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    return apiClient
      .get<unknown, { data: PageResponse<BatchExecutionListItem> }>(
        `/scheduler-monitor/batch/jobs/${encodeURIComponent(params.jobName)}/executions?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /scheduler-monitor/batch/executions/{executionId} — 배치 실행 상세 (SCHEDULER_READ) */
  getBatchExecution: (executionId: number): Promise<JobExecutionDto> =>
    apiClient
      .get<unknown, { data: JobExecutionDto }>(
        `/scheduler-monitor/batch/executions/${executionId}`
      )
      .then((res) => res.data),

  /** POST /scheduler-monitor/batch/jobs/{jobName}/trigger — 수동 트리거 202 (SCHEDULER_TRIGGER) */
  triggerBatchJob: (
    jobName: string,
    params?: Record<string, string>
  ): Promise<TriggerRequestResponse> =>
    apiClient
      .post<unknown, { data: TriggerRequestResponse }>(
        `/scheduler-monitor/batch/jobs/${encodeURIComponent(jobName)}/trigger`,
        { params: params ?? {} }
      )
      .then((res) => res.data),

  /** GET /scheduler-monitor/trigger-requests/{id} — 트리거 요청 단건 (SCHEDULER_READ) */
  getTriggerRequest: (id: number): Promise<TriggerRequestResponse> =>
    apiClient
      .get<unknown, { data: TriggerRequestResponse }>(
        `/scheduler-monitor/trigger-requests/${id}`
      )
      .then((res) => res.data),
};
